/**
 * Carousel Module
 * Custom vanilla JS carousel implementation (no Bootstrap required)
 */

class Carousel {
    constructor(element, options = {}) {
        this.carousel = element;
        this.interval = options.interval || 10000;
        this.isPaused = false;
        this.isSliding = false;
        this.currentIndex = 0;
        this.timer = null;

        this.items = Array.from(this.carousel.querySelectorAll('.carousel-item'));
        this.indicators = Array.from(this.carousel.querySelectorAll('.carousel-indicators li'));
        this.prevButton = this.carousel.querySelector('.carousel-control-prev');
        this.nextButton = this.carousel.querySelector('.carousel-control-next');

        // Text navigation (banner--slider-nav)
        this.textNav = document.querySelector('.banner--slider-nav');
        this.textNavItems = this.textNav ? Array.from(this.textNav.querySelectorAll('li')) : [];

        this.init();
    }

    init() {
        if (this.items.length === 0) return;

        // Set first item as active if none are active
        const hasActive = this.items.some(item => item.classList.contains('active'));
        if (!hasActive) {
            this.items[0].classList.add('active');
        }

        // Find current active index
        this.currentIndex = this.items.findIndex(item => item.classList.contains('active'));
        if (this.currentIndex === -1) this.currentIndex = 0;

        // Set matching indicator as active
        if (this.indicators.length > 0 && this.currentIndex < this.indicators.length) {
            this.indicators.forEach(ind => ind.classList.remove('active'));
            this.indicators[this.currentIndex].classList.add('active');
        }

        // Set matching text nav as active
        if (this.textNavItems.length > 0 && this.currentIndex < this.textNavItems.length) {
            this.textNavItems.forEach(nav => nav.classList.remove('active'));
            this.textNavItems[this.currentIndex].classList.add('active');
        }

        // Bind controls
        if (this.prevButton) {
            this.prevButton.addEventListener('click', () => this.prev());
        }
        if (this.nextButton) {
            this.nextButton.addEventListener('click', () => this.next());
        }

        // Bind indicators
        this.indicators.forEach((indicator, index) => {
            indicator.addEventListener('click', () => this.goTo(index));
        });

        // Bind text navigation
        this.textNavItems.forEach((navItem) => {
            navItem.addEventListener('click', () => {
                const slideIndex = parseInt(navItem.getAttribute('data-slide'));
                if (!isNaN(slideIndex)) {
                    this.goTo(slideIndex);
                }
            });
        });

        // Keyboard navigation
        this.carousel.addEventListener('keydown', (e) => this.handleKeyboard(e));

        // Pause on hover
        this.carousel.addEventListener('mouseenter', () => this.pause());
        this.carousel.addEventListener('mouseleave', () => this.cycle());

        // Touch support
        this.initTouchSupport();

        // Start cycling if not reduced motion
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            this.cycle();
        }

        // Pause when not visible
        this.initVisibilityObserver();
    }

    cycle() {
        if (this.isPaused) return;

        this.stop();
        this.timer = setInterval(() => {
            this.next();
        }, this.interval);
    }

    stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }
    }

    pause() {
        this.isPaused = true;
        this.stop();
    }

    resume() {
        this.isPaused = false;
        this.cycle();
    }

    next() {
        if (this.isSliding) return;
        const nextIndex = (this.currentIndex + 1) % this.items.length;
        this.slideTo(nextIndex, 'next');
    }

    prev() {
        if (this.isSliding) return;
        const prevIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
        this.slideTo(prevIndex, 'prev');
    }

    goTo(index) {
        if (this.isSliding || index === this.currentIndex) return;
        const direction = index > this.currentIndex ? 'next' : 'prev';
        this.slideTo(index, direction);
    }

    slideTo(nextIndex, direction) {
        this.isSliding = true;

        const currentItem = this.items[this.currentIndex];
        const nextItem = this.items[nextIndex];

        // Update indicators
        if (this.indicators.length > 0) {
            this.indicators[this.currentIndex].classList.remove('active');
            this.indicators[nextIndex].classList.add('active');
        }

        // Update text navigation
        if (this.textNavItems.length > 0) {
            this.textNavItems[this.currentIndex].classList.remove('active');
            this.textNavItems[nextIndex].classList.add('active');
        }

        // Perform slide animation
        const directionClass = direction === 'next' ? 'carousel-item-next' : 'carousel-item-prev';
        const orderClass = direction === 'next' ? 'carousel-item-left' : 'carousel-item-right';

        nextItem.classList.add(directionClass);

        // Trigger reflow to ensure the position is set before animation
        nextItem.offsetHeight;

        // Trigger the slide animation
        currentItem.classList.add(orderClass);
        nextItem.classList.add(orderClass);

        // Wait for transition to complete
        const transitionEnd = () => {
            currentItem.classList.remove('active', 'carousel-item-left', 'carousel-item-right');
            nextItem.classList.remove(directionClass, orderClass);
            nextItem.classList.add('active');

            this.currentIndex = nextIndex;
            this.isSliding = false;

            currentItem.removeEventListener('transitionend', transitionEnd);
        };

        currentItem.addEventListener('transitionend', transitionEnd);

        // Fallback in case transitionend doesn't fire
        setTimeout(() => {
            if (this.isSliding) {
                transitionEnd();
            }
        }, 650);
    }

    handleKeyboard(e) {
        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                this.prev();
                break;
            case 'ArrowRight':
                e.preventDefault();
                this.next();
                break;
            case ' ':
            case 'Spacebar':
                e.preventDefault();
                if (this.isPaused) {
                    this.resume();
                } else {
                    this.pause();
                }
                break;
        }
    }

    initTouchSupport() {
        let touchStartX = 0;
        let touchEndX = 0;

        this.carousel.addEventListener('touchstart', (e) => {
            touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.carousel.addEventListener('touchend', (e) => {
            touchEndX = e.changedTouches[0].screenX;
            this.handleSwipe(touchStartX, touchEndX);
        }, { passive: true });
    }

    handleSwipe(startX, endX) {
        const diff = startX - endX;
        const threshold = 50;

        if (Math.abs(diff) > threshold) {
            if (diff > 0) {
                this.next();
            } else {
                this.prev();
            }
        }
    }

    initVisibilityObserver() {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                            this.resume();
                        }
                    } else {
                        this.pause();
                    }
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(this.carousel);
    }
}

/**
 * Initialize carousel enhancements
 */
export const initCarousel = () => {
    const carousels = document.querySelectorAll('.carousel');

    carousels.forEach((carousel) => {
        const interval = parseInt(carousel.getAttribute('data-interval')) || 10000;
        new Carousel(carousel, { interval });
    });
};
